import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { useGetCompaniesList } from "hooks/useInventory"
import {
  getVehicleImporterStaffs,
  createVehicleImporterStaff,
} from "api/vehicleImporterStaffApi"
import { uploadImages } from "api/imageApi"
import { useFormik } from "formik"
import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"
import { Button, Col, Form, Input, Label, Row, Spinner } from "reactstrap"

const SENDER_COMPANY = "Aurobil Cars"
const SENDER_COMPANY_PHONE = "01911887731"
const ATTACHMENT_FIELDS = [
  {
    key: "driver_photo",
    label: "Driver Photo",
    hint: "Upload a clear face photo of the receiving driver.",
  },
  {
    key: "driving_licence",
    label: "Driving Licence",
    hint: "Upload the driver's licence photo clearly.",
  },
]

const isFile = value =>
  typeof File !== "undefined" && value instanceof File

const clickInput = inputId => {
  const input = document.getElementById(inputId)
  if (input) input.click()
}

const HandoverAttachmentInput = ({
  fieldKey,
  label,
  hint,
  value,
  disabled,
  onChange,
  onClear,
}) => {
  const inputId = `handover-${fieldKey}`
  const [objectUrl, setObjectUrl] = useState(null)
  const previewUrl = objectUrl || (value && value.url)

  useEffect(() => {
    if (!isFile(value)) {
      setObjectUrl(null)
      return undefined
    }

    const nextUrl = URL.createObjectURL(value)
    setObjectUrl(nextUrl)
    return () => URL.revokeObjectURL(nextUrl)
  }, [value])

  return (
    <Col md={6} className="mb-3">
      <div className="border rounded-3 bg-light p-3 h-100">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <div>
            <Label className="fw-semibold mb-1">{label}</Label>
            <div className="text-muted small">{hint}</div>
          </div>
          {value ? (
            <span
              className={`badge rounded-pill ${
                isFile(value)
                  ? "bg-warning bg-opacity-10 text-warning"
                  : "bg-success bg-opacity-10 text-success"
              }`}
            >
              {isFile(value) ? "New" : "Saved"}
            </span>
          ) : null}
        </div>

        {previewUrl ? (
          <div>
            <img
              src={previewUrl}
              alt={label}
              className="w-100 rounded-2 bg-white"
              style={{ height: 150, objectFit: "cover" }}
            />
            <div className="d-flex gap-2 mt-2">
              <Button
                type="button"
                size="sm"
                color="light"
                onClick={() => clickInput(inputId)}
                disabled={disabled}
              >
                <i className="bx bx-upload me-1" />
                Replace
              </Button>
              <Button
                type="button"
                size="sm"
                color="light"
                onClick={onClear}
                disabled={disabled}
              >
                <i className="bx bx-trash me-1" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="border border-dashed rounded-3 bg-white p-4 text-center"
            style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            onClick={() => {
              if (!disabled) clickInput(inputId)
            }}
          >
            <i className="bx bx-cloud-upload fs-2 text-primary" />
            <div className="fw-medium small mt-2">Upload {label}</div>
          </div>
        )}

        <Input
          id={inputId}
          type="file"
          accept="image/*"
          className="d-none"
          disabled={disabled}
          onChange={event => {
            const file =
              event.target.files && event.target.files.length
                ? event.target.files[0]
                : null
            if (file) onChange(file)
            event.target.value = ""
          }}
        />
      </div>
    </Col>
  )
}

const HandoverFieldsModal = ({
  isOpen,
  toggle,
  initialData,
  onConfirm,
  employeesData = [],
}) => {
  const [receiverCompanyId, setReceiverCompanyId] = useState(
    initialData?.receiver?.importer_id ?? null
  )
  const [receiverStaffId, setReceiverStaffId] = useState(
    initialData?.receiver?.staff_id ?? null
  )
  const [companySearch, setCompanySearch] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const prevCompanyRef = useRef(null)

  // Companies with nested staff for receiver selection
  const { data: companiesQuery, isLoading: companiesLoading } =
    useGetCompaniesList({ search: companySearch })
  const companies = useMemo(
    () => companiesQuery?.data?.data ?? companiesQuery?.data ?? [],
    [companiesQuery]
  )

  const companyOptions = useMemo(
    () =>
      companies.map(c => ({
        value: String(c.id),
        label: c.name,
        staff: c.staff ?? [],
        raw: c,
      })),
    [companies]
  )

  const selectedCompany = useMemo(
    () => companies.find(c => String(c.id) === String(receiverCompanyId)) ?? null,
    [companies, receiverCompanyId]
  )

  const staffOptions = useMemo(
    () =>
      (selectedCompany?.staff ?? []).map(s => ({
        value: String(s.id),
        label: s.name,
        raw: s,
      })),
    [selectedCompany]
  )

  const employeeOptions = useMemo(
    () => employeesData.map(e => ({ value: String(e.id), label: e.name })),
    [employeesData]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      sender_employee_id: initialData?.sender?.employee_id
        ? String(initialData.sender.employee_id)
        : "",
      sender_name: initialData?.sender?.name ?? "",
      sender_mobile: initialData?.sender?.mobile ?? "",
      sender_designation: initialData?.sender?.designation ?? "",
      receiver_company_id: initialData?.receiver?.importer_id
        ? String(initialData.receiver.importer_id)
        : "",
      receiver_staff_id: initialData?.receiver?.staff_id
        ? String(initialData.receiver.staff_id)
        : "",
      receiver_name: initialData?.receiver?.name ?? "",
      receiver_mobile: initialData?.receiver?.mobile ?? "",
      receiver_designation: initialData?.receiver?.designation ?? "",
      receiver_company: initialData?.receiver?.company ?? "",
      receiver_company_mobile: initialData?.receiver?.company_mobile ?? "",
      driver_photo: initialData?.receiver?.driver_photo ?? null,
      driving_licence: initialData?.receiver?.driving_licence ?? null,
      tyre_size: initialData?.tyre_size ?? "",
      issue_1: initialData?.issues?.[0] ?? "",
      issue_2: initialData?.issues?.[1] ?? "",
      issue_3: initialData?.issues?.[2] ?? "",
      issue_4: initialData?.issues?.[3] ?? "",
      issue_5: initialData?.issues?.[4] ?? "",
      issue_6: initialData?.issues?.[5] ?? "",
    },
    onSubmit: async values => {
      setSubmitting(true)
      try {
        let resolvedStaffId = receiverStaffId

        // Check-or-create importer driver when not selected from dropdown
        if (!resolvedStaffId && receiverCompanyId && values.receiver_mobile) {
          const staffListRes = await getVehicleImporterStaffs(receiverCompanyId, {
            all: true,
          })
          const staffList = staffListRes?.data ?? []
          const existing = staffList.find(s =>
            Array.isArray(s.mobile)
              ? s.mobile.includes(values.receiver_mobile)
              : s.mobile === values.receiver_mobile
          )
          if (existing) {
            resolvedStaffId = existing.id
          } else {
            const created = await createVehicleImporterStaff({
              importerId: receiverCompanyId,
              name: values.receiver_name,
              mobile: [values.receiver_mobile],
              designation: values.receiver_designation,
            })
            resolvedStaffId = created?.data?.id ?? null
          }
        }

        const pendingAttachments = ATTACHMENT_FIELDS.filter(({ key }) =>
          isFile(values[key])
        )
        let uploaded = []

        if (pendingAttachments.length > 0) {
          const formData = new FormData()
          pendingAttachments.forEach(({ key }) => {
            formData.append("images[]", values[key])
          })
          formData.append("max_kb", "100")

          const uploadRes = await uploadImages(formData)
          uploaded = Array.isArray(uploadRes && uploadRes.data)
            ? uploadRes.data
            : []
        }

        let uploadIndex = 0
        const attachmentPayload = ATTACHMENT_FIELDS.reduce((acc, { key }) => {
          if (isFile(values[key])) {
            const image = uploaded[uploadIndex++]
            acc[key] = image
              ? { id: image.id, url: image.url, size: image.size || 0 }
              : null
          } else {
            acc[key] = values[key] || null
          }
          return acc
        }, {})

        const handover = {
          sender: {
            employee_id: values.sender_employee_id
              ? Number(values.sender_employee_id)
              : null,
            name: values.sender_name,
            mobile: values.sender_mobile,
            designation: values.sender_designation,
          },
          receiver: {
            staff_id: resolvedStaffId,
            importer_id: receiverCompanyId ? Number(receiverCompanyId) : null,
            name: values.receiver_name,
            mobile: values.receiver_mobile,
            designation: values.receiver_designation,
            company: values.receiver_company,
            company_mobile: values.receiver_company_mobile,
            driver_photo: attachmentPayload.driver_photo,
            driving_licence: attachmentPayload.driving_licence,
          },
          tyre_size: values.tyre_size,
          issues: [
            values.issue_1,
            values.issue_2,
            values.issue_3,
            values.issue_4,
            values.issue_5,
            values.issue_6,
          ],
        }

        onConfirm(handover)
        toggle()
      } catch (err) {
        toast.error("Failed to save handover details.")
        console.error(err)
      } finally {
        setSubmitting(false)
      }
    },
  })

  // Auto-fill sender from the selected employee option (no extra fetch)
  useEffect(() => {
    const empId = formik.values.sender_employee_id
    if (!empId) return
    const emp = employeesData.find(e => String(e.id) === String(empId))
    if (!emp) return
    formik.setFieldValue("sender_name", emp.name ?? "")
    formik.setFieldValue(
      "sender_mobile",
      Array.isArray(emp.mobile) ? emp.mobile[0] ?? "" : emp.mobile ?? ""
    )
    formik.setFieldValue("sender_designation", emp.designation?.title ?? "")
  }, [formik.values.sender_employee_id, employeesData])

  // Auto-fill company fields when importer is selected
  useEffect(() => {
    const currentCompanyId = formik.values.receiver_company_id
    if (prevCompanyRef.current === currentCompanyId) return
    prevCompanyRef.current = currentCompanyId

    if (!currentCompanyId) {
      setReceiverCompanyId(null)
      return
    }
    setReceiverCompanyId(Number(currentCompanyId))

    const company = companies.find(c => String(c.id) === currentCompanyId)
    if (!company) return

    formik.setFieldValue("receiver_company", company.name ?? "")
    formik.setFieldValue(
      "receiver_company_mobile",
      Array.isArray(company.mobile)
        ? company.mobile[0] ?? ""
        : company.mobile ?? ""
    )
    // Reset staff when company changes
    formik.setFieldValue("receiver_staff_id", "")
    setReceiverStaffId(null)
  }, [formik.values.receiver_company_id, companies])

  // Auto-fill staff fields when driver is selected
  useEffect(() => {
    const staffId = formik.values.receiver_staff_id
    if (!staffId || !selectedCompany) return

    const staff = selectedCompany.staff?.find(s => String(s.id) === staffId)
    if (!staff) return

    setReceiverStaffId(Number(staffId))
    formik.setFieldValue("receiver_name", staff.name ?? "")
    formik.setFieldValue(
      "receiver_mobile",
      Array.isArray(staff.mobile) ? staff.mobile[0] ?? "" : staff.mobile ?? ""
    )
    formik.setFieldValue("receiver_designation", staff.designation ?? "")
  }, [formik.values.receiver_staff_id, selectedCompany])

  return (
    <ThemeModal
      isOpen={isOpen}
      toggle={toggle}
      title="Hand Over Details"
      size="lg"
    >
      <Form onSubmit={formik.handleSubmit}>
        <Row>
          {/* ── Sender Info ───────────────────────── */}
          <Col sm={12} className="mb-2">
            <h6 className="text-muted fw-semibold border-bottom pb-1">
              Sender Info
            </h6>
          </Col>

          <Col sm={12} className="mb-3">
            <FormikCreatableSelect
              name="sender_employee_id"
              label="Select Employee"
              formik={formik}
              options={employeeOptions}
              placeholder="Search employee..."
            />
          </Col>

          <Col sm={4} className="mb-3">
            <Label>Name</Label>
            <InputField
              name="sender_name"
              value={formik.values.sender_name}
              onChange={formik.handleChange}
              placeholder="Sender name"
            />
          </Col>
          <Col sm={4} className="mb-3">
            <Label>Mobile</Label>
            <InputField
              name="sender_mobile"
              value={formik.values.sender_mobile}
              onChange={formik.handleChange}
              placeholder="Sender mobile"
            />
          </Col>
          <Col sm={4} className="mb-3">
            <Label>Designation</Label>
            <InputField
              name="sender_designation"
              value={formik.values.sender_designation}
              onChange={formik.handleChange}
              placeholder="Sender designation"
            />
          </Col>
          <Col sm={6} className="mb-3">
            <Label>Company</Label>
            <InputField value={SENDER_COMPANY} disabled readOnly />
          </Col>
          <Col sm={6} className="mb-3">
            <Label>Company Mobile</Label>
            <InputField value={SENDER_COMPANY_PHONE} disabled readOnly />
          </Col>

          {/* ── Receiver ──────────── */}
          <Col sm={12} className="mb-2">
            <h6 className="text-muted fw-semibold border-bottom pb-1">
              Receiver
            </h6>
          </Col>

          <Col sm={6} className="mb-3">
            <FormikCreatableSelect
              name="receiver_company_id"
              label="Importer (Company)"
              formik={formik}
              options={companyOptions}
              placeholder="Search importer..."
              isLoading={companiesLoading}
              onInputChange={v => setCompanySearch(v)}
            />
          </Col>
          <Col sm={6} className="mb-3">
            <FormikCreatableSelect
              name="receiver_staff_id"
              label="Importer Driver"
              formik={formik}
              options={staffOptions}
              placeholder={
                selectedCompany ? "Select driver..." : "Select company first"
              }
              isDisabled={!selectedCompany}
            />
          </Col>

          <Col sm={4} className="mb-3">
            <Label>Name</Label>
            <InputField
              name="receiver_name"
              value={formik.values.receiver_name}
              onChange={formik.handleChange}
              placeholder="Receiver name"
            />
          </Col>
          <Col sm={4} className="mb-3">
            <Label>Mobile</Label>
            <InputField
              name="receiver_mobile"
              value={formik.values.receiver_mobile}
              onChange={formik.handleChange}
              placeholder="Receiver mobile"
            />
          </Col>
          <Col sm={4} className="mb-3">
            <Label>Designation</Label>
            <InputField
              name="receiver_designation"
              value={formik.values.receiver_designation}
              onChange={formik.handleChange}
              placeholder="Receiver designation"
            />
          </Col>
          <Col sm={6} className="mb-3">
            <Label>Company</Label>
            <InputField
              name="receiver_company"
              value={formik.values.receiver_company}
              onChange={formik.handleChange}
              placeholder="Company name"
            />
          </Col>
          <Col sm={6} className="mb-3">
            <Label>Company Mobile</Label>
            <InputField
              name="receiver_company_mobile"
              value={formik.values.receiver_company_mobile}
              onChange={formik.handleChange}
              placeholder="Company mobile"
            />
          </Col>

          <Col sm={12} className="mb-2">
            <h6 className="text-muted fw-semibold border-bottom pb-1">
              Driver Attachments
            </h6>
          </Col>
          {ATTACHMENT_FIELDS.map(({ key, label, hint }) => (
            <HandoverAttachmentInput
              key={key}
              fieldKey={key}
              label={label}
              hint={hint}
              value={formik.values[key]}
              disabled={submitting}
              onChange={file => formik.setFieldValue(key, file)}
              onClear={() => formik.setFieldValue(key, null)}
            />
          ))}

          {/* ── Issues ───────────────────────────── */}
          <Col sm={12} className="mb-2">
            <h6 className="text-muted fw-semibold border-bottom pb-1">
              Issues (উল্লেখিত সমস্যা সমূহ)
            </h6>
          </Col>
          {[1, 2, 3, 4, 5, 6].map(n => (
            <Col sm={6} className="mb-3" key={n}>
              <Label>{n}.</Label>
              <InputField
                name={`issue_${n}`}
                value={formik.values[`issue_${n}`]}
                onChange={formik.handleChange}
                placeholder={`Issue ${n}`}
              />
            </Col>
          ))}
        </Row>

        <div className="text-end">
          <Button
            color="secondary"
            type="button"
            className="me-2"
            onClick={toggle}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button color="success" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Saving...
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default HandoverFieldsModal
